import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://nutrimartrd.com";

// Per-route SEO. Spanish + DR-targeted keywords to win local search.
// Titles ≤ 60 chars, descriptions 120-160 chars.
const META: Record<string, { title: string; description: string }> = {
  "/": {
    title: "NutriMarket: Suplementos y Vitaminas en RD | Envío Nacional",
    description: "Compra suplementos, proteína, creatina y vitaminas en República Dominicana. Marcas originales, envío a todo el país y pago contra entrega.",
  },
  "/products": {
    title: "Catálogo de Suplementos y Proteína en RD | NutriMarket",
    description: "Explora proteína whey, creatina, BCAA, multivitamínicos y más. Productos originales con envío a Santo Domingo, Santiago y todo RD.",
  },
  "/detail": {
    title: "Detalle de Producto | NutriMarket RD",
    description: "Información, ingredientes, reseñas y precio en pesos dominicanos. Compra suplementos originales con envío en República Dominicana.",
  },
  "/stackBuilder": {
    title: "Arma tu Stack de Suplementos | NutriMarket RD",
    description: "Construye tu combinación ideal de suplementos según tu objetivo: fuerza, energía, recuperación o bienestar. Recomendaciones personalizadas.",
  },
  "/aiChat": {
    title: "Asesor IA de Nutrición y Suplementos | NutriMarket",
    description: "Pregúntale a nuestro asesor de inteligencia artificial qué suplemento te conviene según tus metas, edad y estilo de vida.",
  },
  "/nutricoach": {
    title: "NutriCoach: Coach Personal de Nutrición | NutriMarket RD",
    description: "Recibe planes y consejos de nutrición personalizados para tu objetivo. Coach digital diseñado para el estilo de vida dominicano.",
  },
  "/scanner": {
    title: "Escáner de Etiquetas de Suplementos | NutriMarket",
    description: "Escanea el código de barras o la etiqueta de cualquier suplemento y conoce ingredientes, calidad y alternativas disponibles en RD.",
  },
  "/tracker": {
    title: "Tracker de Suplementos y Hábitos | NutriMarket",
    description: "Registra tu consumo diario de suplementos, vitaminas y hábitos saludables. Mantén tu rutina de bienestar bajo control.",
  },
  "/rewards": {
    title: "Recompensas y Puntos NutriMarket | Ahorra en RD",
    description: "Gana puntos en cada compra y canjéalos por descuentos en suplementos y vitaminas. Programa de recompensas exclusivo en RD.",
  },
  "/referral": {
    title: "Refiere y Gana | Programa de Referidos NutriMarket",
    description: "Invita a tus amigos a NutriMarket y gana crédito para tus próximas compras de suplementos en República Dominicana.",
  },
  "/community": {
    title: "Comunidad NutriMarket | Bienestar y Fitness en RD",
    description: "Conecta con dominicanos apasionados por el bienestar, comparte rutinas, recetas y aprende de expertos en nutrición.",
  },
  "/learn": {
    title: "Guías de Suplementos, Nutrición y Bienestar | NutriMarket",
    description: "Artículos y guías sobre proteína, creatina, vitaminas, recuperación y bienestar, escritas para el público dominicano.",
  },
  "/stores": {
    title: "Tiendas y Puntos de Entrega | NutriMarket RD",
    description: "Encuentra tiendas asociadas y puntos de retiro de NutriMarket en Santo Domingo, Santiago, La Vega y otras ciudades de RD.",
  },
  "/compare": {
    title: "Compara Suplementos lado a lado | NutriMarket",
    description: "Compara precios, ingredientes y reseñas de suplementos disponibles en República Dominicana. Toma la mejor decisión de compra.",
  },
  "/favorites": {
    title: "Mis Favoritos | NutriMarket",
    description: "Tus suplementos y productos de bienestar favoritos en un solo lugar.",
  },
  "/checkout": {
    title: "Finalizar Compra | NutriMarket",
    description: "Completa tu pedido de suplementos en NutriMarket con envío seguro a toda República Dominicana.",
  },
  "/orders": {
    title: "Mis Pedidos | NutriMarket",
    description: "Consulta el estado de tus pedidos de suplementos en NutriMarket.",
  },
  "/profile": {
    title: "Mi Perfil | NutriMarket",
    description: "Gestiona tu cuenta, direcciones y preferencias en NutriMarket.",
  },
};

export function RouteSEO() {
  const { pathname } = useLocation();
  // Standalone pages own their full head (canonical + JSON-LD). Skip
  // RouteSEO for them so we don't ship duplicate <title>/<canonical> tags.
  if (pathname.startsWith("/learn/")) return null;
  if (pathname.startsWith("/marca/")) return null;
  if (pathname.startsWith("/objetivo/")) return null;
  const meta = META[pathname] || META["/"];
  const canonical = `${SITE}${pathname === "/" ? "/" : pathname}`;
  // Hide non-public surfaces from indexing.
  const noIndex = /^\/(admin|vendor|notifications|healthReport|bodyScan|wearable|aiAgent|reset-password|unsubscribe|auth)/.test(pathname);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {noIndex && <meta name="robots" content="noindex, follow" />}
    </Helmet>
  );
}